import { useExplorerSort } from './useExplorerSort';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowLeft, ArrowUp, ClipboardPaste, Copy, Download, Eye, File, Folder, LoaderCircle, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api, fileUrl } from '../../api/client';
import { cancelFileBrowserUpload, newFileUploadId, uploadFileBrowserFile, type BrowserUploadSource, type FileBrowserUploadTarget } from '../../api/fileBrowserUpload';
import { useAuth } from '../../auth/AuthProvider';
import { ErrorState, LoadingState } from '../../components/AsyncState';
import { Modal } from '../../components/Modal';
import { nativeFolderLink } from '../../lib/folderLink';
import { canPreviewFile, FilePreview } from './FilePreview';
import { copyFiles, decodeFiles, droppedFiles, encodeFiles, FILE_CLIPBOARD, getCopiedFiles, hasFiles, isEditable, remoteFile, type FileReference } from './fileClipboard';
import './file-browser-input.css';
import { AssetExplorer } from './Explorer';
import { FolderModeButton } from './FolderModeButton';

export interface FileBrowserTarget {
  assetId: number;
  title: string;
  paths: Record<string, string>;
  pathName?: string;
}

type SortKey = 'name' | 'modified' | 'size';
interface UploadBatch { target: FileBrowserUploadTarget; items: Array<{ file: BrowserUploadSource; uploadId: string; complete: boolean; started?: boolean; cancelled?: boolean }> }

function formatSize(value: number) {
  if (!value) return '—';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
}

export function LegacyFileBrowser({ target, closeBlocked, onUnfinishedChange }: { target: FileBrowserTarget; closeBlocked?: boolean; onUnfinishedChange?: (value: boolean) => void }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [pathName, setPathName] = useState('');
  const [relativePath, setRelativePath] = useState('');
  const [preview, setPreview] = useState('');
  const [sort, setSort] = useExplorerSort();
  const [progress, setProgress] = useState(0);
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [unfinished, setUnfinished] = useState(false);
  const batch = useRef<UploadBatch | null>(null);
  const uploadController = useRef<AbortController | null>(null);
  const activeRequest = useRef<Promise<unknown> | null>(null);
  useEffect(() => () => uploadController.current?.abort(), []);
  useEffect(() => {
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      if (!batch.current?.items.some(item => !item.complete && !item.cancelled)) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, []);

  const listing = useQuery({
    queryKey: ['files', target.assetId, pathName, relativePath],
    queryFn: () => api.files(target.assetId, pathName, relativePath),
    enabled: Boolean(pathName),
  });
  const upload = useMutation({
    mutationFn: async (value: UploadBatch) => {
      const controller = new AbortController();
      uploadController.current = controller;
      const total = value.items.filter(item => !item.cancelled).reduce((sum, item) => sum + item.file.size, 0);
      let done = value.items.filter(item => item.complete).reduce((sum, item) => sum + item.file.size, 0);
      for (const item of value.items) {
        controller.signal.throwIfAborted();
        if (item.complete || item.cancelled) continue;
        item.started = true;
        setUploadLabel(item.file.name);
        const request = uploadFileBrowserFile(item.file, value.target, item.uploadId, (received) => {
          setProgress(total ? (done + received) / total : 1);
          setUploadLabel(`${item.file.name} · ${formatSize(received)} / ${formatSize(item.file.size)}`);
        }, setUploadStatus, controller.signal);
        activeRequest.current = request;
        const result = await request;
        item.complete = true;
        done += item.file.size;
        if (result.filename && result.filename !== item.file.name) setUploadLabel(`${item.file.name} → ${result.filename}`);
      }
      setProgress(1);
      setUploadStatus('Загрузка завершена');
    },
    onSettled: (_data, _error, value) => {
      if (batch.current === value) setUnfinished(value.items.some(item => !item.complete && !item.cancelled));
      return queryClient.invalidateQueries({ queryKey: ['files', value.target.assetId, value.target.pathName, value.target.relativePath] });
    },
  });
  const cancel = useMutation({
    mutationFn: async (value: UploadBatch) => {
      uploadController.current?.abort();
      await activeRequest.current?.catch(() => {});
      setUploadStatus('Отменяем загрузку…');
      for (const item of value.items) {
        if (item.complete || item.cancelled) continue;
        if (!item.started) { item.cancelled = true; continue; }
        const result = await cancelFileBrowserUpload(item.file, value.target, item.uploadId);
        if (result.status === 'cancelled') item.cancelled = true;
        else item.complete = true;
      }
      setUploadStatus(value.items.some(item => item.complete) ? 'Незавершённые загрузки отменены. Уже сохранённые файлы оставлены.' : 'Загрузка отменена');
    },
    onSettled: (_data, _error, value) => {
      if (batch.current === value) setUnfinished(value.items.some(item => !item.complete && !item.cancelled));
      return queryClient.invalidateQueries({ queryKey: ['files', value.target.assetId, value.target.pathName, value.target.relativePath] });
    },
  });
  useEffect(() => {
    onUnfinishedChange?.(unfinished || cancel.isPending);
  }, [onUnfinishedChange, unfinished, cancel.isPending]);

  const segments = relativePath.split('/').filter(Boolean);
  const sortedFiles = useMemo(() => (listing.data?.files ?? []).slice().sort((left, right) => {
    const folderOrder = Number(right.is_folder) - Number(left.is_folder);
    if (folderOrder) return folderOrder;
    const result = sort.key === 'name'
      ? left.filename.localeCompare(right.filename, 'ru', { numeric: true })
      : left[sort.key] - right[sort.key];
    return (sort.ascending ? result : -result)
      || left.filename.localeCompare(right.filename, 'ru', { numeric: true });
  }), [listing.data?.files, sort]);
  const changeSort = (key: SortKey) => {
    setSort((current) => current.key === key
      ? { key, ascending: !current.ascending }
      : { key, ascending: key === 'name' });
  };
  const sortIcon = (key: SortKey) => sort.key === key
    ? sort.ascending ? <ArrowUp aria-hidden="true" size={13} /> : <ArrowDown aria-hidden="true" size={13} />
    : null;
  const enter = (folder: string) => setRelativePath(`${relativePath}${folder}/`);
  const back = () => {
    if (preview) {
      setPreview('');
      return;
    }
    if (segments.length) setRelativePath(segments.slice(0, -1).join('/') + (segments.length > 1 ? '/' : ''));
    else setPathName('');
  };

  useEffect(() => { root.current?.focus({ preventScroll: true }); }, [pathName, relativePath, preview]);
  const busy = upload.isPending || unfinished || cancel.isPending;
  const startUpload = (files: BrowserUploadSource[], destination = relativePath) => {
    if (!files.length) return;
    if (!pathName) { setNotice('Сначала откройте папку для загрузки.'); return; }
    if (preview) { setNotice('Вернитесь из просмотра файла в папку.'); return; }
    // Ref guards a second drop/paste before React has rendered the first batch.
    if (busy || batch.current?.items.some(item => !item.complete && !item.cancelled)) { setNotice('Дождитесь завершения текущей передачи или отмените её.'); return; }
    const value = { target: { assetId: target.assetId, pathName, relativePath: destination }, items: files.map(file => ({ file, uploadId: newFileUploadId(), complete: false })) };
    batch.current = value;
    setUnfinished(true); setProgress(0); setUploadStatus(''); setNotice('');
    cancel.reset(); upload.mutate(value);
  };
  const references = (): FileReference[] => sortedFiles.filter(item => !item.is_folder && selected.includes(`${pathName}/${relativePath}${item.filename}`)).map(item => ({ assetId: target.assetId, pathName, path: relativePath + item.filename, name: item.filename, size: item.size, modified: item.modified }));
  const copySelected = () => {
    const files = references();
    if (files.length) { copyFiles(files); setNotice(`Скопировано файлов: ${files.length}. Откройте папку назначения и нажмите Ctrl+V или «Вставить».`); }
    return files;
  };
  const receive = (data: DataTransfer, destination = relativePath) => {
    try {
      if (hasFiles(data) || data.files?.length) startUpload(droppedFiles(data), destination);
      else {
        const files = decodeFiles(data.getData(FILE_CLIPBOARD) || data.getData('text/plain'));
        if (files.length) startUpload(files.map(remoteFile), destination);
      }
    } catch (error) { setNotice(error instanceof Error ? error.message : 'Не удалось прочитать файлы.'); }
  };
  const inside = (element: EventTarget | null) => element instanceof Node && root.current?.contains(element);
  const canReceive = (data: DataTransfer) => hasFiles(data) || Array.from(data.types).includes(FILE_CLIPBOARD);

  return (
      <div className={`file-browser${dragging ? ' is-file-dragging' : ''}`} ref={root} tabIndex={-1} role="region" aria-label="Проводник файлов"
        onPointerDown={event => { if (event.target instanceof Element && !event.target.closest('button,a,input,textarea,[contenteditable]')) root.current?.focus({ preventScroll: true }); }}
        onCopy={event => {
          if (!inside(event.target) || preview || isEditable(event.target) || window.getSelection()?.toString()) return;
          const files = copySelected(); if (!files.length) return;
          event.preventDefault(); event.stopPropagation();
          event.clipboardData.setData(FILE_CLIPBOARD, encodeFiles(files)); event.clipboardData.setData('text/plain', encodeFiles(files));
        }}
        onPaste={event => {
          if (!inside(event.target) || preview || isEditable(event.target)) return;
          if (!hasFiles(event.clipboardData) && !event.clipboardData.files?.length && !decodeFiles(event.clipboardData.getData(FILE_CLIPBOARD) || event.clipboardData.getData('text/plain')).length) return;
          event.preventDefault(); event.stopPropagation(); receive(event.clipboardData);
        }}
        onDragEnter={event => { if (inside(event.target) && canReceive(event.dataTransfer)) { event.preventDefault(); event.stopPropagation(); dragDepth.current++; setDragging(true); } }}
        onDragLeave={event => { if (canReceive(event.dataTransfer)) { event.preventDefault(); event.stopPropagation(); if (--dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false); } } }}
        onDragOver={event => { if (inside(event.target) && canReceive(event.dataTransfer)) { event.preventDefault(); event.stopPropagation(); event.dataTransfer.dropEffect = busy || !pathName || Boolean(preview) ? 'none' : 'copy'; } }}
        onDrop={event => {
          if (!inside(event.target) || !canReceive(event.dataTransfer)) return;
          event.preventDefault(); event.stopPropagation(); dragDepth.current = 0; setDragging(false);
          const folder = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-file-folder]')?.dataset.fileFolder : undefined;
          receive(event.dataTransfer, folder ?? relativePath);
        }}>
        {pathName ? (
          <div className="toolbar">
            <button className="button button--secondary" disabled={upload.isPending || unfinished || cancel.isPending} onClick={back} type="button"><ArrowLeft size={16} /> Назад</button>
            <span className="toolbar__spacer" />
            {!preview ? <><button type="button" className="button button--secondary" title="Копировать выбранные файлы · Ctrl+C" disabled={!references().length || busy} onClick={() => {
              const files = copySelected();
              if (files.length) void navigator.clipboard?.writeText(encodeFiles(files)).catch(() => {});
            }}><Copy size={16}/><span>Копировать</span></button>
            <button type="button" className="button button--secondary" title="Вставить скопированные в Скрепке файлы" disabled={!getCopiedFiles().length || busy} onClick={() => startUpload(getCopiedFiles().map(remoteFile))}><ClipboardPaste size={16}/><span>Вставить</span></button></> : null}
            <input aria-label="Выбрать файлы для загрузки" hidden multiple onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = '';
              startUpload(files);
            }} ref={input} type="file" />
            <button className="button button--secondary" disabled={upload.isPending || unfinished || cancel.isPending} onClick={() => input.current?.click()} type="button">
              {upload.isPending ? <LoaderCircle className="spin" size={16} /> : <Upload size={16} />}
              {upload.isPending ? ` Загрузка… ${Math.round(progress * 100)}%` : ' Загрузить'}
            </button>
            {unfinished || cancel.isPending ? <button className="button button--secondary" disabled={cancel.isPending} onClick={() => batch.current && cancel.mutate(batch.current)} type="button">{cancel.isPending ? 'Отменяем…' : 'Отменить загрузку'}</button> : null}
          </div>
        ) : null}

        {pathName && !preview ? <div className="file-browser__location"><strong>{pathName}{relativePath ? ` / ${relativePath.replace(/\/$/, '')}` : ''}</strong><span>Перетащите файлы сюда или вставьте через Ctrl+V</span></div> : null}
        <div className="file-browser__content">
          {!pathName ? (
            <div className="folder-grid">
              {Object.entries(target.paths).map(([name, path]) => {
                const native = nativeFolderLink(path, Boolean(user?.from_local_net));
                return path.startsWith('https://') ? (
                  <a className="folder-card" href={path} key={name} rel="noreferrer" target="_blank"><Folder size={28} /><strong>{name}</strong><small>{path}</small></a>
                ) : native ? (
                  <div className="folder-choice" key={name}>
                    <button className="folder-card" onClick={() => setPathName(name)} type="button"><Folder size={28} /><strong>{name}</strong><small>{path}</small><span>В браузере</span></button>
                    <a className="button button--secondary" href={native.href} title={native.title}>{native.title}</a>
                  </div>
                ) : (
                  <button className="folder-card" key={name} onClick={() => setPathName(name)} type="button"><Folder size={28} /><strong>{name}</strong><small>{path}</small></button>
                );
              })}
            </div>
          ) : null}
          {listing.isPending && pathName ? <LoadingState label="Читаем папку…" /> : null}
          {listing.error ? <ErrorState error={listing.error} retry={() => void listing.refetch()} /> : null}
          {preview ? (
            <FilePreview assetId={target.assetId} key={preview} path={preview} pathName={pathName} onClose={() => setPreview('')} />
          ) : listing.data ? (
            <div className="file-table" role="table">
              <div className="file-row file-row--header" role="row">
                <button aria-label="Сортировать по названию" aria-pressed={sort.key === 'name'} onClick={() => changeSort('name')} type="button">Название {sortIcon('name')}</button>
                <button aria-label="Сортировать по дате" aria-pressed={sort.key === 'modified'} onClick={() => changeSort('modified')} type="button">Дата {sortIcon('modified')}</button>
                <button aria-label="Сортировать по размеру" aria-pressed={sort.key === 'size'} onClick={() => changeSort('size')} type="button">Размер {sortIcon('size')}</button>
                <span />
                <span />
              </div>
              {sortedFiles.map((item) => {
                  const path = `${relativePath}${item.filename}`;
                  const viewable = !item.is_folder && canPreviewFile(item.filename);
                  return (
                    <div className="file-row" key={item.filename} role="row" data-file-folder={item.is_folder ? `${path}/` : undefined}>
                      {item.is_folder ? (
                        <button className="file-row__name" disabled={upload.isPending || unfinished || cancel.isPending} onClick={() => enter(item.filename)} type="button"><Folder size={20} /><span>{item.filename}</span></button>
                      ) : (
                        <div className="file-browser__file"><input type="checkbox" aria-label={`Выбрать ${item.filename}`} checked={selected.includes(`${pathName}/${path}`)} disabled={busy} onChange={event => setSelected(current => event.target.checked ? [...current, `${pathName}/${path}`] : current.filter(name => name !== `${pathName}/${path}`))}/>
                        <a className="file-row__name" href={fileUrl(target.assetId, pathName, path, true)} download={item.filename} draggable={!busy} onDragStart={event => {
                          const files = selected.includes(`${pathName}/${path}`) ? references() : [{ assetId: target.assetId, pathName, path, name: item.filename, size: item.size, modified: item.modified }];
                          event.dataTransfer.setData(FILE_CLIPBOARD, encodeFiles(files)); event.dataTransfer.effectAllowed = 'copy';
                        }}><File size={20} /><span>{item.filename}</span></a></div>
                      )}
                      <time>{new Intl.DateTimeFormat('ru-RU').format(new Date(item.modified * 1000))}</time>
                      <span>{formatSize(item.size)}</span>
                      {viewable ? <button aria-label="Просмотр" className="icon-button" onClick={() => setPreview(path)} type="button"><Eye size={18} /></button> : <span />}
                      <a aria-label={`Скачать ${item.filename}`} className="icon-button" href={fileUrl(target.assetId, pathName, path, true)} download={item.is_folder ? `${item.filename}.zip` : item.filename}><Download size={18} /></a>
                    </div>
                  );
                })}
            </div>
          ) : null}
        </div>
        {dragging ? <div className="file-browser__drop-hint" aria-live="polite"><Upload size={28}/><strong>{busy ? 'Идёт передача файлов' : !pathName ? 'Сначала откройте папку' : preview ? 'Вернитесь в папку' : 'Отпустите файлы для копирования'}</strong></div> : null}
        {notice ? <p className="file-browser__notice" role="status">{notice}</p> : null}
        {uploadLabel ? <p role="status">{uploadLabel}{uploadStatus ? ` · ${uploadStatus}` : ''}</p> : null}
        {upload.isPending ? <p>Не закрывайте это окно и вкладку до завершения загрузки.</p> : null}
        {closeBlocked && unfinished ? <p className="form-error">Чтобы закрыть окно, дождитесь завершения или нажмите «Отменить загрузку».</p> : null}
        {cancel.error ? <div className="form-error"><p>{cancel.error.message} Очередь сохранена — отмену можно повторить.</p><button className="button button--secondary" onClick={() => {
          if (!window.confirm('Сервер не подтвердил отмену. Временные части файлов могли остаться в папке назначения. После закрытия очереди продолжить эту загрузку отсюда будет нельзя. Закрыть очередь?')) return;
          uploadController.current?.abort();
          batch.current = null;
          setUnfinished(false);
          setUploadStatus('Очередь закрыта. Удаление временных частей не подтверждено.');
          upload.reset();
          cancel.reset();
        }} type="button">Закрыть очередь</button></div> : null}
        {upload.error && unfinished && !cancel.isPending ? <div className="form-error"><p>{upload.error.name === 'AbortError' ? 'Передача остановлена.' : upload.error.message}</p><button className="button button--secondary" onClick={() => { cancel.reset(); if (batch.current) upload.mutate(batch.current); }} type="button">Повторить</button></div> : null}
      </div>
  );
}

export function LegacyFileBrowserModal({ target, onClose }: { target: FileBrowserTarget | null; onClose: () => void }) {
  const unfinished = useRef(false);
  const [closeBlocked, setCloseBlocked] = useState(false);
  if (!target) return null;
  const title = `${target.title}`;
  const close = () => {
    if (unfinished.current) { setCloseBlocked(true); return; }
    onClose();
  };
  return (
    <Modal className="file-browser-modal" onClose={close} size="large" title={title}>
      <LegacyFileBrowser closeBlocked={closeBlocked} key={target.assetId} onUnfinishedChange={value => { unfinished.current = value; }} target={target} />
    </Modal>
  );
}

export function FileBrowser({ target }: { target: FileBrowserTarget; closeBlocked?: boolean; onUnfinishedChange?: (value: boolean) => void }) {
  return <AssetExplorer key={`${target.assetId}:${target.pathName ?? ''}`} target={target}/>;
}

export function FileBrowserModal({ target, onClose }: { target: FileBrowserTarget | null; onClose: () => void }) {
  const { user } = useAuth();
  return target ? <Modal className="sx-browser-modal" onClose={onClose} size="wide" title={target.title} headerToolbar={user ? <FolderModeButton userId={user.id}/> : null}><FileBrowser target={target}/></Modal> : null;
}
